import { useEffect, useState, useMemo } from "react";
import api, { errMsg, formatTL } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { Upload, Download, FileSpreadsheet, Building2, Factory, AlertTriangle, CheckCircle2, TrendingUp, TrendingDown, Plus, Trash2, X, Filter } from "lucide-react";
import { toast } from "sonner";

const monthShort = (m) => {
  if (!m) return "-";
  const [y, mo] = m.split("-");
  const names = ["Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"];
  return `${names[parseInt(mo, 10) - 1] || mo} ${y?.slice(2)}`;
};

const emptyHakedis = () => ({
  entity_type: "company",
  entity_id: "",
  entity_name: "",
  month: new Date().toISOString().slice(0,7),
  hakedis_no: "",
  amount: 0,
  description: "",
});

export default function HakedisComparisonPage() {
  const [tab, setTab] = useState("company"); // "company" | "supplier"
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [onlyMismatch, setOnlyMismatch] = useState(false);
  const [search, setSearch] = useState("");

  // Upload state
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Toplu cari hakediş raporu (rptA) state
  const [showBulk, setShowBulk] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkMonth, setBulkMonth] = useState("auto");
  const [bulkReplace, setBulkReplace] = useState(true);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [missingSel, setMissingSel] = useState([]);
  const [creatingMissing, setCreatingMissing] = useState(false);

  // Manual add state
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(emptyHakedis());
  const [companies, setCompanies] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  // Hakediş list view (for delete)
  const [showList, setShowList] = useState(false);
  const [hakedisList, setHakedisList] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/hakedis/comparison?entity_type=${tab}${year ? `&year=${year}` : ""}`);
      setComparison(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [tab, year]);

  useEffect(() => {
    // Load entities for the add form
    (async () => {
      try {
        const [cs, ss] = await Promise.all([api.get("/companies"), api.get("/suppliers")]);
        setCompanies(cs.data || []);
        setSuppliers(ss.data || []);
      } catch (e) { /* silent */ }
    })();
  }, []);

  const loadHakedisList = async () => {
    try {
      const { data } = await api.get(`/hakedis?entity_type=${tab}`);
      // Since generic CRUD may not filter, filter client-side
      const filtered = (data || []).filter(h => h.entity_type === tab);
      // Sort by month desc
      filtered.sort((a, b) => (b.month || "").localeCompare(a.month || ""));
      setHakedisList(filtered);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const submitUpload = async () => {
    if (!uploadFile) { toast.error("Excel dosyası seçin"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("entity_type", tab);
      const { data } = await api.post("/hakedis/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const unmatched = data.unmatched_entity_names?.length || 0;
      toast.success(`${data.inserted} kayıt eklendi${data.skipped ? ` · ${data.skipped} atlandı` : ""}${unmatched ? ` · ${unmatched} eşleşmeyen isim` : ""}`);
      setShowUpload(false);
      setUploadFile(null);
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setUploading(false); }
  };

  const bulkMonthOptions = (() => {
    const arr = [];
    const d = new Date();
    const names = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];
    for (let i = 0; i < 18; i++) {
      arr.push({ value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: `${names[d.getMonth()]} ${d.getFullYear()}` });
      d.setMonth(d.getMonth() - 1);
    }
    return arr;
  })();

  const submitBulk = async () => {
    if (!bulkFile) { toast.error("Excel dosyası seçin"); return; }
    setBulkUploading(true);
    setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append("file", bulkFile);
      fd.append("month", bulkMonth === "auto" ? "" : bulkMonth);
      fd.append("replace", bulkReplace);
      const { data } = await api.post("/hakedis/upload-report", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBulkResult(data);
      setMissingSel(data.unmatched_entity_names || []);
      toast.success(`${data.inserted} cari hakedişi yüklendi (${monthShort(data.month)})${data.replaced ? ` · ${data.replaced} eski kayıt değiştirildi` : ""}`);
      // Yüklenen ayın karşılaştırmasını göster
      setTab("supplier");
      setYear(data.month.slice(0, 4));
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBulkUploading(false); }
  };

  const createMissing = async () => {
    if (missingSel.length === 0) { toast.error("Kart oluşturulacak cari seçin"); return; }
    setCreatingMissing(true);
    try {
      const { data } = await api.post("/hakedis/create-missing-suppliers", { names: missingSel });
      toast.success(`${data.created} tedarikçi kartı oluşturuldu · ${data.linked_hakedis} hakediş kaydı bağlandı`);
      setBulkResult((prev) => prev ? { ...prev, unmatched_entity_names: (prev.unmatched_entity_names || []).filter((n) => !missingSel.includes(n)) } : prev);
      setMissingSel([]);
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setCreatingMissing(false); }
  };

  const downloadTemplate = async () => {
    try {
      const resp = await api.get(`/hakedis/template?entity_type=${tab}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Hakedis_Sablonu_${tab === "company" ? "firma" : "tedarikci"}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!addForm.entity_name.trim()) { toast.error("İsim zorunlu"); return; }
    if (!/^\d{4}-\d{2}$/.test(addForm.month)) { toast.error("Geçerli ay (YYYY-MM)"); return; }
    if (Number(addForm.amount) <= 0) { toast.error("Tutar pozitif olmalı"); return; }
    try {
      await api.post("/hakedis", { ...addForm, entity_type: tab, amount: Number(addForm.amount) });
      toast.success("Hakediş kaydı eklendi");
      setShowAdd(false);
      setAddForm(emptyHakedis());
      await load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const deleteHakedis = async (id) => {
    if (!window.confirm("Bu hakediş kaydı silinsin mi?")) return;
    try {
      await api.delete(`/hakedis/${id}`);
      toast.success("Silindi");
      await loadHakedisList();
      await load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const filteredRows = useMemo(() => {
    if (!comparison) return [];
    const q = search.trim().toLowerCase();
    return comparison.rows.filter(r => {
      if (onlyMismatch && Math.abs(r.totals.diff) < 0.5) return false;
      if (q && !(r.entity_name || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [comparison, search, onlyMismatch]);

  const entityOptions = tab === "company" ? companies : suppliers;

  return (
    <>
      <PageHeader
        title="Fatura ↔ Hakediş Fark Analizi"
        subtitle="Hakediş yükleyip fatura toplamlarıyla karşılaştırın. Farklar firma / tedarikçi bazında ay ay listelenir."
        testid="hakedis-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button
              data-testid="btn-hakedis-template"
              onClick={downloadTemplate}
              className="inline-flex items-center gap-2 border border-[#E5E5E5] px-3 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#F5F5F5]"
            >
              <Download className="w-3.5 h-3.5" /> Excel Şablon
            </button>
            <button
              data-testid="btn-hakedis-upload"
              onClick={() => setShowUpload(true)}
              className="inline-flex items-center gap-2 bg-[#0020C2] text-white px-3 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#0018A0]"
            >
              <Upload className="w-3.5 h-3.5" /> Excel Yükle
            </button>
            <button
              data-testid="btn-hakedis-bulk-report"
              onClick={() => { setShowBulk(true); setBulkResult(null); setBulkFile(null); setBulkMonth("auto"); }}
              className="inline-flex items-center gap-2 bg-[#7C3AED] text-white px-3 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#6D28D9]"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" /> Toplu Cari Hakediş
            </button>
            <button
              data-testid="btn-hakedis-add"
              onClick={() => setShowAdd(true)}
              className="inline-flex items-center gap-2 bg-[#16A34A] text-white px-3 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#15803D]"
            >
              <Plus className="w-3.5 h-3.5" /> Manuel Ekle
            </button>
            <button
              data-testid="btn-hakedis-list"
              onClick={() => { setShowList(true); loadHakedisList(); }}
              className="inline-flex items-center gap-2 border border-[#E5E5E5] px-3 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#F5F5F5]"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" /> Hakediş Listesi
            </button>
          </div>
        }
      />

      {/* Tab switcher */}
      <div className="flex items-center gap-1 mb-4 border-b border-[#E5E5E5]">
        <button
          data-testid="tab-company"
          onClick={() => setTab("company")}
          className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-widest font-semibold border-b-2 transition-colors ${tab === "company" ? "border-[#0020C2] text-[#0020C2]" : "border-transparent text-neutral-500 hover:text-neutral-800"}`}
        >
          <Building2 className="w-3.5 h-3.5" /> Firma Bazlı (Müşteri Hakedişi)
        </button>
        <button
          data-testid="tab-supplier"
          onClick={() => setTab("supplier")}
          className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-widest font-semibold border-b-2 transition-colors ${tab === "supplier" ? "border-[#0020C2] text-[#0020C2]" : "border-transparent text-neutral-500 hover:text-neutral-800"}`}
        >
          <Factory className="w-3.5 h-3.5" /> Tedarikçi Bazlı
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E5E5E5] p-3 rounded-md mb-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-neutral-400" />
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Yıl</label>
          <input
            data-testid="filter-year"
            type="number" min="2020" max="2100" value={year}
            onChange={e => setYear(e.target.value)}
            className="w-24 px-2 py-1.5 text-sm border border-[#E5E5E5] num"
          />
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <input
            data-testid="filter-search"
            placeholder={`${tab === "company" ? "Firma" : "Tedarikçi"} adı ara...`}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 px-3 py-1.5 text-sm border border-[#E5E5E5]"
          />
        </div>
        <button
          data-testid="filter-only-mismatch"
          onClick={() => setOnlyMismatch(!onlyMismatch)}
          className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold border ${onlyMismatch ? "bg-[#DC2626] text-white border-[#DC2626]" : "border-[#E5E5E5] hover:bg-[#F5F5F5]"}`}
        >
          Sadece Farklı Olanlar
        </button>
      </div>

      {loading ? (
        <div className="text-center py-16 text-sm text-neutral-500">Yükleniyor...</div>
      ) : !comparison ? null : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <div className="bg-white border border-[#E5E5E5] p-4">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">{tab === "company" ? "Firma" : "Tedarikçi"} Sayısı</div>
              <div className="num font-bold text-xl mt-1">{comparison.grand.entities}</div>
            </div>
            <div className="bg-white border border-[#E5E5E5] p-4">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Fatura Toplamı</div>
              <div className="num font-bold text-xl mt-1 text-[#0020C2]">{formatTL(comparison.grand.invoice)}</div>
            </div>
            <div className="bg-white border border-[#E5E5E5] p-4">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Hakediş Toplamı</div>
              <div className="num font-bold text-xl mt-1 text-[#7C3AED]">{formatTL(comparison.grand.hakedis)}</div>
            </div>
            <div className="bg-white border border-[#E5E5E5] p-4">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Net Fark</div>
              <div className={`num font-bold text-xl mt-1 ${comparison.grand.diff > 0 ? "text-[#DC2626]" : comparison.grand.diff < 0 ? "text-[#F59E0B]" : "text-[#16A34A]"}`}>
                {comparison.grand.diff > 0 ? "+" : ""}{formatTL(comparison.grand.diff)}
              </div>
              <div className="text-[10px] text-neutral-500 mt-0.5">
                {comparison.grand.diff > 0 ? "Fatura > Hakediş (fazla kesildi)" : comparison.grand.diff < 0 ? "Hakediş > Fatura (eksik kesildi)" : "Denk"}
              </div>
            </div>
            <div className="bg-white border border-[#E5E5E5] p-4">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Uyumsuz</div>
              <div className="num font-bold text-xl mt-1 text-[#DC2626]">{comparison.grand.mismatches}</div>
              <div className="text-[10px] text-neutral-500 mt-0.5">Toplam mutlak fark: {formatTL(comparison.grand.abs_diff)}</div>
            </div>
          </div>

          {/* Comparison table */}
          {filteredRows.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-[#E5E5E5] rounded-md">
              <CheckCircle2 className="w-10 h-10 mx-auto text-neutral-400 mb-2" />
              <div className="text-sm font-semibold text-neutral-600">
                {comparison.rows.length === 0
                  ? (tab === "company" ? "Henüz firma bazlı hakediş verisi yok. Excel yükleyin veya manuel ekleyin." : "Henüz tedarikçi bazlı hakediş verisi yok.")
                  : "Filtreye uygun kayıt bulunamadı"}
              </div>
            </div>
          ) : (
            <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                    <tr>
                      <th className="px-3 py-3 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">{tab === "company" ? "Firma" : "Tedarikçi"}</th>
                      <th className="px-3 py-3 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Fatura</th>
                      <th className="px-3 py-3 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Hakediş</th>
                      <th className="px-3 py-3 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Fark</th>
                      <th className="px-3 py-3 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Fark %</th>
                      <th className="px-3 py-3 text-center text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Ay Sayısı</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map(r => {
                      const d = r.totals.diff;
                      const isMismatch = Math.abs(d) > 0.5;
                      return (
                        <tr key={r.key} className="border-b border-[#F5F5F5]" data-testid={`hakedis-row-${r.key}`}>
                          <td className="px-3 py-3">
                            <div className="font-semibold text-sm">{r.entity_name}</div>
                            <div className="text-[10px] text-neutral-500">
                              {isMismatch ? (
                                <span className="inline-flex items-center gap-1 text-[#DC2626] font-semibold">
                                  <AlertTriangle className="w-3 h-3" /> Uyumsuz
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-[#16A34A] font-semibold">
                                  <CheckCircle2 className="w-3 h-3" /> Denk
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-3 text-right num text-[#0020C2] font-semibold">{formatTL(r.totals.invoice)}</td>
                          <td className="px-3 py-3 text-right num text-[#7C3AED] font-semibold">{formatTL(r.totals.hakedis)}</td>
                          <td className={`px-3 py-3 text-right num font-bold ${d > 0 ? "text-[#DC2626]" : d < 0 ? "text-[#F59E0B]" : "text-[#16A34A]"}`}>
                            <span className="inline-flex items-center gap-1 justify-end">
                              {d > 0 ? <TrendingUp className="w-3 h-3" /> : d < 0 ? <TrendingDown className="w-3 h-3" /> : null}
                              {d > 0 ? "+" : ""}{formatTL(d)}
                            </span>
                          </td>
                          <td className={`px-3 py-3 text-right num font-semibold ${Math.abs(r.totals.diff_pct) > 5 ? "text-[#DC2626]" : "text-neutral-600"}`}>
                            {r.totals.diff_pct > 0 ? "+" : ""}{r.totals.diff_pct}%
                          </td>
                          <td className="px-3 py-3 text-center num text-neutral-600">{r.totals.months_count}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-[#FAFAFA] border-t-2 border-[#E5E5E5]">
                    <tr>
                      <td className="px-3 py-3 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">GENEL TOPLAM</td>
                      <td className="px-3 py-3 text-right num font-bold text-[#0020C2]">{formatTL(comparison.grand.invoice)}</td>
                      <td className="px-3 py-3 text-right num font-bold text-[#7C3AED]">{formatTL(comparison.grand.hakedis)}</td>
                      <td className={`px-3 py-3 text-right num font-bold ${comparison.grand.diff > 0 ? "text-[#DC2626]" : comparison.grand.diff < 0 ? "text-[#F59E0B]" : "text-[#16A34A]"}`}>
                        {comparison.grand.diff > 0 ? "+" : ""}{formatTL(comparison.grand.diff)}
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Monthly matrix — mismatch-only rows */}
          {comparison.months.length > 0 && filteredRows.some(r => Math.abs(r.totals.diff) > 0.5) && (
            <div className="mt-6">
              <div className="text-xs font-semibold uppercase tracking-widest text-neutral-600 mb-2">Aylık Fark Matrisi (Sadece Uyumsuzlar)</div>
              <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                      <tr>
                        <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold sticky left-0 bg-[#FAFAFA]">{tab === "company" ? "Firma" : "Tedarikçi"}</th>
                        {comparison.months.map(m => (
                          <th key={m} className="px-2 py-2 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold min-w-[90px]">{monthShort(m)}</th>
                        ))}
                        <th className="px-2 py-2 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold min-w-[100px]">TOPLAM</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.filter(r => Math.abs(r.totals.diff) > 0.5).map(r => (
                        <tr key={r.key} className="border-b border-[#F5F5F5]">
                          <td className="px-3 py-2 sticky left-0 bg-white font-semibold text-neutral-800">{r.entity_name}</td>
                          {comparison.months.map(m => {
                            const cell = r.months[m];
                            if (!cell) return <td key={m} className="px-2 py-2 text-right text-neutral-300">-</td>;
                            const d = cell.diff;
                            const cls = Math.abs(d) < 0.5 ? "text-neutral-400" : d > 0 ? "text-[#DC2626] font-semibold" : "text-[#F59E0B] font-semibold";
                            return (
                              <td key={m} className={`px-2 py-2 text-right num ${cls}`} title={`Fatura: ${formatTL(cell.invoice)} · Hakediş: ${formatTL(cell.hakedis)}`}>
                                {d > 0 ? "+" : ""}{formatTL(d)}
                              </td>
                            );
                          })}
                          <td className={`px-2 py-2 text-right num font-bold border-l border-[#E5E5E5] ${r.totals.diff > 0 ? "text-[#DC2626]" : "text-[#F59E0B]"}`}>
                            {r.totals.diff > 0 ? "+" : ""}{formatTL(r.totals.diff)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Upload modal */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setShowUpload(false)}>
          <div className="bg-white w-full max-w-md rounded-md shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Excel Yükle</div>
                <div className="font-heading font-bold text-lg">{tab === "company" ? "Firma" : "Tedarikçi"} Hakedişleri</div>
              </div>
              <button onClick={() => setShowUpload(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-[#EFF6FF] border border-[#0020C2] p-3 text-xs text-[#1E3A8A]">
                Excel sütunları: <b>{tab === "company" ? "FİRMA ADI" : "TEDARİKÇİ ADI"}</b> | <b>AY (YYYY-MM)</b> | <b>HAKEDİŞ NO</b> | <b>TUTAR</b> | <b>AÇIKLAMA</b>
              </div>
              <input
                data-testid="hakedis-file"
                type="file" accept=".xlsx,.xls"
                onChange={e => setUploadFile(e.target.files[0])}
                className="w-full text-sm"
              />
              {uploadFile && <div className="text-xs text-neutral-600">Seçilen: {uploadFile.name}</div>}
              <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button onClick={() => setShowUpload(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button
                  data-testid="hakedis-upload-submit"
                  disabled={uploading || !uploadFile}
                  onClick={submitUpload}
                  className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0020C2] text-white hover:bg-[#0018A0] disabled:opacity-50"
                >{uploading ? "Yükleniyor..." : "Yükle"}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toplu cari hakediş raporu modal */}
      {showBulk && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setShowBulk(false)}>
          <div className="bg-white w-full max-w-lg rounded-md shadow-lg max-h-[88vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="bulk-report-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplu Yükleme</div>
                <div className="font-heading font-bold text-lg">Cari Hakediş Raporu</div>
              </div>
              <button onClick={() => setShowBulk(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-[#F5F3FF] border border-[#7C3AED]/40 p-3 text-xs text-[#5B21B6]">
                "PROJE CARİ HAKEDİŞ LİSTESİ" formatındaki Excel'i yükleyin. Her carinin servis satırları toplanır ve
                seçilen ay için <b>tedarikçi hakedişi</b> olarak kaydedilir; ardından o ayın faturalarıyla otomatik karşılaştırılır.
              </div>
              <input
                data-testid="bulk-report-file"
                type="file" accept=".xlsx,.xls"
                onChange={e => setBulkFile(e.target.files[0])}
                className="w-full text-sm"
              />
              {bulkFile && <div className="text-xs text-neutral-600">Seçilen: {bulkFile.name}</div>}
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Dönem (Ay)</label>
                <select data-testid="bulk-report-month" value={bulkMonth} onChange={e => setBulkMonth(e.target.value)} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm">
                  <option value="auto">Dosyadaki dönemi kullan (otomatik)</option>
                  {bulkMonthOptions.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" data-testid="bulk-report-replace" checked={bulkReplace} onChange={e => setBulkReplace(e.target.checked)} className="accent-[#7C3AED]" />
                Bu ayın önceki toplu yüklemesi varsa üzerine yaz (mükerrer kaydı önler)
              </label>

              {bulkResult && (
                <div className="border border-[#E5E5E5]" data-testid="bulk-report-result">
                  <div className="grid grid-cols-3 divide-x divide-[#F5F5F5] text-center">
                    <div className="p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Cari</div>
                      <div className="num font-bold">{bulkResult.inserted}</div>
                    </div>
                    <div className="p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam Sefer</div>
                      <div className="num font-bold">{bulkResult.total_tek}</div>
                    </div>
                    <div className="p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Hakediş Toplamı</div>
                      <div className="num font-bold text-[#7C3AED]">{formatTL(bulkResult.total_amount)}</div>
                    </div>
                  </div>
                  {bulkResult.total_extra > 0 && (
                    <div className="px-3 py-2 border-t border-[#F5F5F5] text-[11px] text-neutral-600">
                      Ana: {formatTL(bulkResult.total_main)} + Ek: {formatTL(bulkResult.total_extra)}
                    </div>
                  )}
                  {bulkResult.unmatched_entity_names?.length > 0 && (
                    <div className="px-3 py-2 border-t border-[#FDE68A] bg-[#FFFBEB] text-[11px] text-[#92400E]">
                      <b>{bulkResult.unmatched_entity_names.length} cari</b> tedarikçi kartıyla eşleşmedi. Kart oluşturulacakları seçin:
                      <div className="mt-1.5 max-h-32 overflow-y-auto space-y-1">
                        {bulkResult.unmatched_entity_names.map((n) => (
                          <label key={n} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={missingSel.includes(n)}
                              onChange={() => setMissingSel((prev) => prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n])}
                              className="accent-[#7C3AED]"
                            />
                            {n}
                          </label>
                        ))}
                      </div>
                      <button
                        data-testid="create-missing-suppliers-btn"
                        disabled={creatingMissing || missingSel.length === 0}
                        onClick={createMissing}
                        className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-widest font-bold bg-[#16A34A] text-white hover:bg-[#15803D] disabled:opacity-50"
                      >
                        <Plus className="w-3.5 h-3.5" /> {creatingMissing ? "Oluşturuluyor..." : `Seçili ${missingSel.length} Cari İçin Tedarikçi Kartı Oluştur`}
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-3 border-t border-[#E5E5E5]">
                <button onClick={() => setShowBulk(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">{bulkResult ? "Kapat" : "Vazgeç"}</button>
                <button
                  data-testid="bulk-report-submit"
                  disabled={bulkUploading || !bulkFile}
                  onClick={submitBulk}
                  className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#7C3AED] text-white hover:bg-[#6D28D9] disabled:opacity-50"
                >{bulkUploading ? "Yükleniyor..." : "Yükle ve Karşılaştır"}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual add modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div className="bg-white w-full max-w-md rounded-md shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Manuel Hakediş Ekle</div>
                <div className="font-heading font-bold text-lg">{tab === "company" ? "Firma Hakedişi" : "Tedarikçi Hakedişi"}</div>
              </div>
              <button onClick={() => setShowAdd(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submitAdd} className="p-6 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">{tab === "company" ? "Firma" : "Tedarikçi"} *</label>
                <input
                  data-testid="add-entity-name"
                  list="entity-options"
                  required
                  value={addForm.entity_name}
                  onChange={e => {
                    const name = e.target.value;
                    const match = entityOptions.find(x => (x.name || "").trim().toLowerCase() === name.trim().toLowerCase());
                    setAddForm({ ...addForm, entity_name: name, entity_id: match?.id || "" });
                  }}
                  placeholder={tab === "company" ? "Firma adı yaz veya seç" : "Tedarikçi adı yaz veya seç"}
                  className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm"
                />
                <datalist id="entity-options">
                  {entityOptions.map(e => <option key={e.id} value={e.name} />)}
                </datalist>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Ay *</label>
                  <input data-testid="add-month" type="month" required value={addForm.month} onChange={e => setAddForm({ ...addForm, month: e.target.value })} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tutar (₺) *</label>
                  <input data-testid="add-amount" type="number" step="any" required value={addForm.amount} onChange={e => setAddForm({ ...addForm, amount: e.target.value })} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Hakediş No</label>
                  <input value={addForm.hakedis_no} onChange={e => setAddForm({ ...addForm, hakedis_no: e.target.value })} placeholder="HK-2026-001" className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Açıklama</label>
                  <input value={addForm.description} onChange={e => setAddForm({ ...addForm, description: e.target.value })} placeholder="Not" className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button data-testid="add-submit" type="submit" className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#16A34A] text-white hover:bg-[#15803D]">Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Hakediş list modal */}
      {showList && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setShowList(false)}>
          <div className="bg-white w-full max-w-4xl max-h-[85vh] rounded-md shadow-lg flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Hakediş Kayıtları</div>
                <div className="font-heading font-bold text-lg">{tab === "company" ? "Firma" : "Tedarikçi"} Hakedişleri · {hakedisList.length} kayıt</div>
              </div>
              <button onClick={() => setShowList(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {hakedisList.length === 0 ? (
                <div className="text-center py-10 text-sm text-neutral-500">Henüz kayıt yok.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                    <tr>
                      <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">{tab === "company" ? "Firma" : "Tedarikçi"}</th>
                      <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Ay</th>
                      <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Hakediş No</th>
                      <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Tutar</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {hakedisList.map(h => (
                      <tr key={h.id} className="border-b border-[#F5F5F5]">
                        <td className="px-3 py-2 font-semibold">{h.entity_name}</td>
                        <td className="px-3 py-2 num">{monthShort(h.month)}</td>
                        <td className="px-3 py-2 text-neutral-600">{h.hakedis_no || "-"}</td>
                        <td className="px-3 py-2 text-right num text-[#7C3AED] font-semibold">{formatTL(h.amount)}</td>
                        <td className="px-3 py-2 text-right">
                          <button data-testid={`hakedis-del-${h.id}`} onClick={() => deleteHakedis(h.id)} className="p-1.5 hover:bg-[#FEE2E2] text-[#DC2626]"><Trash2 className="w-3.5 h-3.5" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
